import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

// A helper component to safely render icons if they exist
const SafeIcon = ({ icon: Icon, ...props }) => {
  return Icon ? <Icon {...props} /> : null;
};

const {
  FiPlay, FiCheck, FiStar, FiUsers, FiTrendingUp, FiTarget,
  FiClock, FiGift, FiShield, FiPhone, FiMail, FiChevronDown,
  FiArrowRight, FiZap, FiDollarSign, FiBookOpen, FiAward, FiX, FiCalendar,
  FiBarChart3, FiCrown, FiTrophy, FiDiamond, FiLock, FiUnlock, FiHome,
  FiMapPin, FiBriefcase, FiPhoneCall, FiMessageCircle, FiBot
} = FiIcons;

// Registration Modal Component (Themed for Real Estate)
const RegistrationModal = ({ isOpen, onClose, formData, setFormData, onSubmit }) => {
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
            className="relative bg-gradient-to-br from-gray-900 via-yellow-900 to-amber-900 border-2 border-yellow-400/50 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl"
          >
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-2">
                Secure Your Agent Seat
              </h2>
              <p className="text-gray-400">Enter your details to proceed to payment.</p>
            </div>
            
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="full_name" className="block text-sm font-bold text-yellow-300 mb-2">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  id="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-bold text-yellow-300 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800/50 border-2 border-yellow-400/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                  placeholder="Enter your email"
                />
              </div>
              {/* Phone Number Input with Country Code */}
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-bold text-yellow-300 mb-2">Phone Number</label>
                <div className="flex items-center">
                  <select
                    name="countryCode"
                    id="countryCode"
                    value={formData.countryCode}
                    onChange={handleChange}
                    className="bg-gray-800/50 border-2 border-r-0 border-yellow-400/30 rounded-l-lg px-3 py-3 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition appearance-none"
                    style={{ backgroundImage: 'none' }}
                  >
                    <option>+91</option>
                    <option>+1</option>
                    <option>+44</option>
                    <option>+61</option>
                    <option>+971</option>
                  </select>
                  <input
                    type="tel"
                    name="phoneNumber"
                    id="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    className="w-full bg-gray-800/50 border-2 border-yellow-400/30 rounded-r-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition"
                    placeholder="98765 43210"
                  />
                </div>
              </div>
              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-yellow-500 via-amber-600 to-yellow-700 text-black font-black py-4 px-6 rounded-xl text-lg shadow-2xl flex items-center justify-center gap-2 border-2 border-yellow-300"
              >
                <SafeIcon icon={FiLock} className="w-5 h-5" />
                Proceed to Payment
                <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
              </motion.button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

function App() {
  // Evergreen timer that resets every 15 minutes
  const [bonusTimeLeft, setBonusTimeLeft] = useState({
    minutes: 14,
    seconds: 59
  });
  const [seatsLeft] = useState(42);
  const [openFaq, setOpenFaq] = useState(null);

  // State for modal visibility and form data
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Updated FormData State with Country Code
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    countryCode: '+91', // Default country code for India
    phoneNumber: ''
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setBonusTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { minutes: prev.minutes - 1, seconds: 59 };
        } else {
          // Reset to 15 minutes when timer reaches zero
          return { minutes: 14, seconds: 59 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const testimonials = [
    {
      name: "Rajesh Kumar",
      role: "Real Estate Agent & Team Leader",
      result: "₹45+ Lakhs Commission",
      period: "6 Months",
      quote: "AI bot என்னோட clients-ஐ 24/7 handle பண்ணுது. Monthly 150+ qualified leads automatically convert ஆகுது!",
      rating: 5,
      multiplier: "15X More Leads",
      experience: "12 Years Experience"
    },
    {
      name: "Priya Nair",
      role: "Property Promoter & Developer",
      result: "₹2.8 Crore Sales",
      period: "4 Months",
      quote: "AI automation use பண்ணி property showcasing, follow-ups எல்லாம் automated. Sales team productivity 300% increase!",
      rating: 5,
      multiplier: "300% Productivity",
      experience: "8 Years Experience"
    },
    {
      name: "Vikram Reddy",
      role: "Independent Real Estate Consultant",
      result: "₹1.2 Cr Revenue",
      period: "3 Months",
      quote: "Single-ஆ work பண்ணினாலும் AI-ன் help-ல 50+ clients simultaneously handle பண்ண முடியுது. Game changer!",
      rating: 5,
      multiplier: "50X Capacity",
      experience: "15 Years Experience"
    }
  ];

  const bonuses = [
    {
      title: "Real Estate AI Bot Builder",
      value: "₹35,000",
      description: "Custom AI chatbot that qualifies leads, schedules visits, and handles property inquiries 24/7",
      icon: FiBot,
      premium: true
    },
    {
      title: "Property Lead Generation System",
      value: "₹25,000",
      description: "Automated lead capture system with WhatsApp integration for instant client communication",
      icon: FiPhoneCall,
      premium: true
    },
    {
      title: "Smart CRM & Follow-up Templates",
      value: "₹15,000",
      description: "AI-powered CRM with automated follow-up sequences for hot leads and client nurturing",
      icon: FiMessageCircle,
      premium: false
    },
    {
      title: "Market Analysis & Pricing Tool",
      value: "₹12,000",
      description: "AI tool for instant property valuation, market trends, and competitive pricing analysis",
      icon: FiBarChart3,
      premium: false
    }
  ];

  const faqs = [
    {
      question: "Real estate business-க்கு AI எப்படி helpful ஆகும்?",
      answer: "AI bots உங்க clients-ஐ 24/7 handle பண்ணும், property details provide பண்ணும், appointments schedule பண்ணும், qualified leads identify பண்ணும். Manual work 80% reduce ஆகும்!"
    },
    {
      question: "Technical knowledge இல்லாம AI tools use பண்ண முடியுமா?",
      answer: "Absolutely! நம்ம workshop-ல simple drag-and-drop tools மட்டும் தான் use பண்ணுவோம். Coding knowledge வேண்டாம். Real estate professionals-க்கு specially designed!"
    },
    {
      question: "Existing clients-ஐ AI-ல integrate பண்ண முடியுமா?",
      answer: "Yes! உங்க current database, WhatsApp, phone numbers எல்லாம் AI system-ல easily import பண்ணலாம். Existing workflow-ல seamlessly integrate ஆகும்."
    },
    {
      question: "ROI எவ்வளவு நாள்ல expect பண்ணலாம்?",
      answer: "First month-ல setup complete, second month-ல lead generation start, third month-ல significant ROI. 90% real estate professionals 5X lead increase achieve பண்ணியிருக்காங்க!"
    },
    {
      question: "Multiple properties handle பண்ண முடியுமா?",
      answer: "Definitely! Residential, commercial, plots, apartments - எல்லா type properties-ம் handle பண்ணும். Multiple projects simultaneously manage பண்ணலாம்!"
    }
  ];
  
  // Track ViewContent event when page loads and send to external tracker
  useEffect(() => {
    // Facebook Pixel tracking
    if (typeof fbq !== 'undefined') {
      fbq('track', 'ViewContent', {
        content_name: 'Real Estate AI Workshop Landing Page',
        content_category: 'Workshop',
        content_type: 'product'
      });
    }

    // External Yinguru tracking for page view
    if (typeof window !== 'undefined' && window.yinguru) {
      window.yinguru.track('PageView', {
        page_title: 'Real Estate AI Workshop Landing Page',
        page_url: window.location.href,
        event_category: 'Workshop',
        event_action: 'Page View'
      });
    }
  }, []);

  // Add External Yinguru Tracker with enhanced functionality
  useEffect(() => {
    // Check if script is already loaded
    if (document.querySelector('script[data-tracking-id="tk_3c2878362376441bbaf8f6bb87d0e34d"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = "https://dmc.yinguru.com/js/external-tracking.js";
    script.setAttribute('data-tracking-id', 'tk_3c2878362376441bbaf8f6bb87d0e34d');
    script.async = true;

    // Add onload event to track page view when script loads
    script.onload = () => {
      if (window.yinguru) {
        window.yinguru.track('PageView', {
          page_title: 'Real Estate AI Workshop Landing Page',
          page_url: window.location.href,
          event_category: 'Workshop',
          event_action: 'Page Load Complete'
        });
      }
    };

    document.head.appendChild(script);

    // Cleanup function to remove the script when the component unmounts
    return () => {
      const existingScript = document.querySelector('script[data-tracking-id="tk_3c2878362376441bbaf8f6bb87d0e34d"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  // Function to open the registration modal
  const openRegistrationModal = () => {
    setIsModalOpen(true);
    
    // Track modal open event
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', {
        content_name: 'Registration Modal Opened',
        content_category: 'Workshop',
        event_action: 'Modal Open'
      });
    }

    // External tracker for modal open
    if (window.yinguru) {
      window.yinguru.track('ModalOpen', {
        modal_name: 'Registration Modal',
        event_category: 'User Interaction',
        event_action: 'Modal Open'
      });
    }
  };

  // Handle form submission: track events and redirect with pre-filled data
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Track Lead event with Meta Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'Lead', {
        content_name: 'Real Estate AI Workshop Registration',
        content_category: 'Workshop',
        value: 2500.00,
        currency: 'INR'
      });
    }
    
    // Track InitiateCheckout event
    if (typeof fbq !== 'undefined') {
      fbq('track', 'InitiateCheckout', {
        content_name: 'Real Estate AI Workshop',
        content_category: 'Education',
        value: 2500.00,
        currency: 'INR',
        num_items: 1
      });
    }

    // External Yinguru tracker for form submission
    if (window.yinguru) {
      window.yinguru.track('FormSubmission', {
        form_name: 'Registration Form',
        event_category: 'Lead Generation',
        event_action: 'Form Submit',
        value: 2500.00,
        currency: 'INR',
        user_data: {
          name: formData.full_name,
          email: formData.email,
          phone: `${formData.countryCode}${formData.phoneNumber}`
        }
      });
    }

    // Combine country code and phone number into one string
    const fullPhoneNumber = `${formData.countryCode}${formData.phoneNumber}`;

    // Pabbly Webhook Integration
    const webhookUrl = "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTY1MDYzZjA0MzQ1MjY5NTUzZDUxMzQi_pc";
    try {
        fetch(webhookUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                full_name: formData.full_name,
                email: formData.email,
                phoneNumber: fullPhoneNumber, // Send the combined number
            }),
        });
    } catch (error) {
        console.error('Webhook submission error:', error);
    }
    
    // Track successful webhook submission
    if (window.yinguru) {
      window.yinguru.track('WebhookSubmission', {
        event_category: 'Data Processing',
        event_action: 'Webhook Success',
        webhook_url: webhookUrl
      });
    }
    
    // Redirect to Payment Page with Pre-filled Data
    const baseUrl = "https://paymentz.aifunnels.app/product/21362/page/18278/addon-page";
    
    const params = new URLSearchParams({
      first_name: formData.full_name,
      email: formData.email,
      phone_no: fullPhoneNumber, // Send the combined number
      staffId: '64304',
      ac: '1'
    });

    const paymentUrl = `${baseUrl}?${params.toString()}`;
    
    // Track payment page redirect
    if (window.yinguru) {
      window.yinguru.track('PaymentRedirect', {
        event_category: 'Conversion',
        event_action: 'Payment Page Redirect',
        payment_url: paymentUrl,
        value: 2500.00,
        currency: 'INR'
      });
    }
    
    window.open(paymentUrl, '_blank');
    setIsModalOpen(false);
  };

  // Floating elements for premium feel
  const FloatingElement = ({ children, delay = 0 }) => (
    <motion.div
      animate={{
        y: [-10, 10, -10],
        rotate: [-1, 1, -1],
      }}
      transition={{
        duration: 6,
        repeat: Infinity,
        delay,
        ease: "easeInOut"
      }}
    >
      {children}
    </motion.div>
  );

  // Mobile-Optimized Sticky Button
  const StickyButton = () => (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-3 bg-gradient-to-t from-black via-black/95 to-transparent backdrop-blur-sm border-t border-yellow-400/20">
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={openRegistrationModal}
        className="w-full bg-gradient-to-r from-yellow-500 via-amber-600 to-yellow-700 text-black font-black py-4 px-6 rounded-2xl text-base shadow-2xl flex items-center justify-center gap-2 border-2 border-yellow-300 mobile-btn"
        style={{
          background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 25%, #d97706 50%, #eab308 75%, #f59e0b 100%)',
          backgroundSize: '300% 300%',
          animation: 'gradient-shift 3s ease infinite'
        }}
      >
        <SafeIcon icon={FiHome} className="w-5 h-5 flex-shrink-0" />
        <span className="font-black text-center leading-tight">
          Reserve My Agent Seat
          <br />
          <span className="text-sm font-bold">Only {seatsLeft} Left!</span>
        </span>
        <SafeIcon icon={FiArrowRight} className="w-5 h-5 flex-shrink-0" />
      </motion.button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-amber-900 to-yellow-900 relative overflow-hidden">
      
      <RegistrationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleFormSubmit}
      />
      
      {/* Premium Background Pattern - Mobile Optimized */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-400 to-amber-600"></div>
        <div className="absolute top-10 left-10 w-48 h-48 sm:w-96 sm:h-96 bg-yellow-400 rounded-full blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 sm:w-80 sm:h-80 bg-amber-500 rounded-full blur-3xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] bg-gradient-radial from-yellow-300 to-transparent opacity-5 animate-spin-slow"></div>
      </div>

      {/* Mobile Sticky Button */}
      <StickyButton />

      {/* Main Content with Bottom Padding for Sticky Button */}
      <div className="pb-24">
        {/* Hero Section - Mobile Optimized */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden px-4">
          {/* Premium Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-amber-900 to-yellow-900"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-yellow-500/10"></div>
          
          {/* Mobile Floating Elements */}
          <FloatingElement delay={0}>
            <div className="absolute top-16 left-6 w-3 h-3 sm:w-4 sm:h-4 bg-yellow-400 rounded-full blur-sm opacity-60"></div>
          </FloatingElement>
          <FloatingElement delay={2}>
            <div className="absolute top-32 right-8 w-4 h-4 sm:w-6 sm:h-6 bg-amber-500 rounded-full blur-sm opacity-40"></div>
          </FloatingElement>
          <FloatingElement delay={4}>
            <div className="absolute bottom-32 left-8 w-2 h-2 sm:w-3 sm:h-3 bg-yellow-300 rounded-full blur-sm opacity-50"></div>
          </FloatingElement>

          <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-4 sm:space-y-6"
            >
              {/* Mobile Premium Trust Badges */}
              <div className="flex flex-wrap justify-center gap-2 mb-4 sm:mb-6">
                <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-full px-3 py-1 text-yellow-300 text-xs font-bold">
                  <SafeIcon icon={FiHome} className="inline mr-1 w-3 h-3" />
                  2000+ Agents Success
                </div>
                <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-full px-3 py-1 text-yellow-300 text-xs font-bold">
                  <SafeIcon icon={FiDollarSign} className="inline mr-1 w-3 h-3" />
                  ₹50+ Crore Sales
                </div>
                <div className="bg-gradient-to-r from-yellow-400/20 to-amber-500/20 backdrop-blur-sm border border-yellow-400/30 rounded-full px-3 py-1 text-yellow-300 text-xs font-bold">
                  <SafeIcon icon={FiBot} className="inline mr-1 w-3 h-3" />
                  AI-Powered System
                </div>
              </div>

              {/* Mobile Premium Headline */}
              <div className="space-y-2 sm:space-y-4">
                <motion.h1 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500 leading-tight px-2"
                  style={{
                    textShadow: '0 0 30px rgba(251, 191, 36, 0.5)',
                  }}
                >
                  AI Agents-ஆல் உங்கள் Property
                </motion.h1>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 }}
                  className="text-3xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-white mb-2 sm:mb-4"
                  style={{
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                  }}
                >
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500">Sales-ஐ</span> 10X பண்ணுங்கள்
                </motion.div>
              </div>

              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="text-lg sm:text-2xl md:text-3xl text-gray-300 max-w-4xl mx-auto leading-relaxed px-2 font-medium"
              >
                <span className="text-yellow-400 font-bold">Real Estate Agents-க்கு:</span> AI-ன் மூலம் 
                <span className="text-amber-400 font-bold"> More Leads, More Sales</span> அடையுங்கள்!
              </motion.p>

              {/* Mobile Premium Workshop Schedule */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
                className="bg-gradient-to-r from-yellow-500/10 to-amber-600/10 backdrop-blur-lg border border-yellow-400/30 rounded-2xl p-4 sm:p-6 max-w-sm mx-auto shadow-2xl"
              >
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiCalendar} className="w-5 h-5 text-black" />
                  </div>
                  <p className="text-yellow-400 font-bold text-base">Real Estate AI Workshop</p>
                </div>
                <div className="text-white text-center space-y-1">
                  <div className="text-xl font-black text-yellow-400">This Sunday</div>
                  <div className="text-base text-gray-300">10:00 AM - 1:00 PM IST</div>
                  <div className="text-xs text-amber-400 font-medium bg-amber-400/10 rounded-full px-3 py-1 inline-block">
                    Limited to 50 Property Professionals
                  </div>
                </div>
              </motion.div>

              {/* Mobile Premium Countdown Timer */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5 }}
                className="bg-gradient-to-r from-red-600/20 to-orange-600/20 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-4 sm:p-6 max-w-xs mx-auto shadow-2xl"
              >
                <div className="flex items-center justify-center gap-1 mb-3">
                  <SafeIcon icon={FiClock} className="w-5 h-5 text-red-400" />
                  <p className="text-red-400 font-bold text-sm text-center">🚨 Agent Bonuses Expire In:</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-3 border border-red-400">
                    <div className="text-2xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-xs text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-3 border border-red-400">
                    <div className="text-2xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-xs text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
                <div className="text-xs text-red-300 mt-3 font-medium text-center">Worth ₹87,000+ Real Estate Tools!</div>
              </motion.div>

              {/* Mobile Premium CTA */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.8 }}
                className="space-y-4 px-2"
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openRegistrationModal}
                  className="bg-gradient-to-r from-yellow-500 via-amber-600 to-yellow-700 text-black font-black py-5 px-8 rounded-2xl text-lg shadow-2xl border-4 border-yellow-300 w-full relative overflow-hidden mobile-btn"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <SafeIcon icon={FiHome} className="w-5 h-5" />
                    My Agent Success Seat-ஐ Book பண்ணுங்கள்
                    <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                </motion.button>
                
                <div className="flex flex-col items-center gap-2">
                  <div className="text-red-400 font-bold text-lg text-center">
                    ⚠️ Only {seatsLeft} Agent Seats Remaining!
                  </div>
                  <div className="text-yellow-400 text-sm font-medium text-center">
                    Join 2000+ Successful Real Estate Professionals
                  </div>
                </div>
              </motion.div>

              {/* Mobile Premium Guarantee */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.1 }}
                className="flex justify-center px-2"
              >
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/50 rounded-full px-4 py-2 text-green-300 text-sm font-bold backdrop-blur-sm text-center">
                  <SafeIcon icon={FiShield} className="inline mr-1 w-4 h-4" />
                  100% Real Estate Success Guarantee
                </div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Problem & Solution Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-amber-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 to-amber-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <div className="space-y-8 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start">
              {/* Problems - Mobile Optimized */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500 mb-6 text-center lg:text-left">
                  உங்கள் Real Estate Business-ல இந்த Problems இருக்கா?
                </h2>
                
                <div className="space-y-4">
                  {[
                    { problem: "Lead generation-க்கு நிறைய time & money spend ஆகுது", icon: FiDollarSign },
                    { problem: "Manual follow-ups miss ஆகி potential clients lose ஆகுது", icon: FiPhoneCall },
                    { problem: "24/7 client queries handle பண்ண முடியலை", icon: FiClock },
                    { problem: "Property details repeatedly explain பண்ணணும்", icon: FiUsers },
                    { problem: "Competition-ல lag ஆகி market share lose ஆகுது", icon: FiTrendingUp }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-400/30 rounded-xl backdrop-blur-sm hover:border-red-400/50 transition-all"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={item.icon} className="text-white w-5 h-5" />
                      </div>
                      <p className="text-gray-300 text-base font-medium leading-snug">{item.problem}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Solutions - Mobile Optimized */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <h2 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-6 text-center lg:text-left">
                  AI Agent Solutions for Real Estate!
                </h2>
                
                <div className="space-y-4">
                  {[
                    { solution: "24/7 AI chatbot automatically leads qualify & capture", icon: FiBot, multiplier: "24/7 Active" },
                    { solution: "Automated follow-up sequences never miss clients", icon: FiMessageCircle, multiplier: "Zero Miss" },
                    { solution: "Instant property details & pricing information", icon: FiHome, multiplier: "Instant Response" },
                    { solution: "Smart appointment scheduling & calendar management", icon: FiCalendar, multiplier: "Auto-Schedule" },
                    { solution: "WhatsApp integration for seamless communication", icon: FiPhoneCall, multiplier: "WhatsApp Ready" }
                  ].map((item, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3 p-4 bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border border-yellow-400/30 rounded-xl backdrop-blur-sm hover:border-yellow-400/50 transition-all group"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={item.icon} className="text-black w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-300 text-base font-medium mb-2 leading-snug">{item.solution}</p>
                        <span className="inline-block bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                          {item.multiplier}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mobile Premium Social Proof Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-amber-900 to-black relative">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/3 to-amber-500/3"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-6">
                Real Estate Professionals Success Stories
              </h2>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-yellow-400">2000+</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Agents</div>
                </div>
                <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-yellow-400">₹50+ Cr</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Sales</div>
                </div>
                <div className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-xl px-3 py-3">
                  <div className="text-xl sm:text-3xl font-black text-yellow-400">95%</div>
                  <div className="text-gray-300 font-medium text-xs sm:text-sm">Success</div>
                </div>
              </div>
            </motion.div>

            <div className="space-y-6 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-b from-gray-800/50 to-amber-900/50 backdrop-blur-lg border border-yellow-400/20 rounded-2xl p-6 shadow-2xl hover:border-yellow-400/40 transition-all"
                >
                  {/* Rating Stars */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <SafeIcon key={i} icon={FiStar} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  
                  {/* Results */}
                  <div className="mb-4">
                    <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">
                      {testimonial.result}
                    </div>
                    <div className="text-gray-400 text-sm mb-2">in {testimonial.period}</div>
                    <div className="inline-block bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full mb-2">
                      {testimonial.multiplier}
                    </div>
                    <div className="text-yellow-300 text-xs font-medium">{testimonial.experience}</div>
                  </div>
                  
                  {/* Quote */}
                  <blockquote className="text-gray-300 mb-4 italic text-sm leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  {/* Author */}
                  <div className="border-t border-gray-700 pt-4">
                    <div className="font-bold text-white text-base">{testimonial.name}</div>
                    <div className="text-yellow-400 text-sm font-medium">{testimonial.role}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium Curriculum Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-amber-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 to-amber-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-4">
                3 Hours = Real Estate Transformation
              </h2>
              <p className="text-lg text-gray-300 font-medium">Complete AI Agent System for Property Professionals</p>
            </motion.div>

            <div className="space-y-6 lg:grid lg:grid-cols-3 lg:gap-6 lg:space-y-0">
              {[
                {
                  hour: "Hour 1",
                  title: "AI Foundation for Agents",
                  subtitle: "From Traditional to AI-Powered",
                  icon: FiZap,
                  color: "from-purple-500 to-yellow-600",
                  items: [
                    "AI chatbot setup for lead qualification",
                    "WhatsApp automation for instant responses", 
                    "Property database integration with AI",
                    "Client communication automation basics"
                  ]
                },
                {
                  hour: "Hour 2", 
                  title: "Lead Generation & Conversion",
                  subtitle: "Maximize Your Sales Pipeline",
                  icon: FiTrendingUp,
                  color: "from-yellow-500 to-amber-600",
                  items: [
                    "Automated lead capture from multiple sources",
                    "Smart follow-up sequences for hot leads",
                    "Appointment scheduling without manual work",
                    "Client nurturing campaigns setup"
                  ]
                },
                {
                  hour: "Hour 3",
                  title: "Scale & Automate",
                  subtitle: "Build Your Property Empire",
                  icon: FiCrown,
                  color: "from-amber-500 to-yellow-600",
                  items: [
                    "Multi-property management system",
                    "Advanced analytics and reporting",
                    "Team collaboration and delegation",
                    "Long-term wealth building strategies"
                  ]
                }
              ].map((module, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-gradient-to-b from-gray-800/50 to-amber-900/50 backdrop-blur-lg border border-yellow-400/20 rounded-2xl p-6 shadow-2xl hover:border-yellow-400/40 transition-all"
                >
                  <div className="text-center mb-6">
                    <div className={`w-16 h-16 bg-gradient-to-r ${module.color} rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <SafeIcon icon={module.icon} className="w-8 h-8 text-black" />
                    </div>
                    <div className="text-sm font-bold text-yellow-400 mb-2">{module.hour}</div>
                    <h3 className="text-xl font-black text-white mb-2">{module.title}</h3>
                    <p className="text-amber-400 font-bold text-base">{module.subtitle}</p>
                  </div>
                  
                  <ul className="space-y-3">
                    {module.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-start gap-2">
                        <div className="w-5 h-5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <SafeIcon icon={FiCheck} className="text-black w-3 h-3" />
                        </div>
                        <span className="text-gray-300 font-medium text-sm leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            {/* Mobile Premium Learning Outcomes */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-gradient-to-r from-yellow-500/20 to-amber-600/20 backdrop-blur-lg border-2 border-yellow-400/30 rounded-2xl p-6 text-center"
            >
              <h3 className="text-2xl sm:text-3xl font-black text-yellow-400 mb-6">Workshop முடிஞ்சதும் உங்ககிட்ட இருக்கும்:</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { outcome: "Complete AI agent system", icon: FiBot },
                  { outcome: "Automated lead pipeline", icon: FiTrendingUp },
                  { outcome: "24/7 client communication", icon: FiPhoneCall },
                  { outcome: "Property sales automation", icon: FiHome }
                ].map((item, index) => (
                  <div key={index} className="flex flex-col items-center gap-2 bg-gradient-to-b from-yellow-400/10 to-amber-500/10 border border-yellow-400/30 rounded-xl p-4">
                    <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={item.icon} className="w-5 h-5 text-black" />
                    </div>
                    <span className="text-white font-bold text-center text-sm leading-tight">{item.outcome}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Bonus Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-amber-900 to-black relative overflow-hidden">
          {/* Premium Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-500/10 to-amber-600/10"></div>
            <FloatingElement delay={0}>
              <div className="absolute top-20 left-20 w-24 h-24 sm:w-32 sm:h-32 bg-yellow-400/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={3}>
              <div className="absolute bottom-20 right-20 w-32 h-32 sm:w-40 sm:h-40 bg-amber-500/20 rounded-full blur-2xl"></div>
            </FloatingElement>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-6">
                ₹87,000+ Real Estate AI Tools - FREE!
              </h2>
              <div className="text-4xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 mb-8">
                Total Value: ₹2,50,000+
              </div>
              
              {/* Mobile Premium Bonus Timer */}
              <div className="bg-gradient-to-r from-red-600/30 to-orange-600/30 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-6 max-w-sm mx-auto shadow-2xl">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <SafeIcon icon={FiClock} className="w-6 h-6 text-red-400" />
                  <p className="text-red-400 font-black text-lg">⏰ Agent Bonuses Expire In:</p>
                </div>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {bonuses.map((bonus, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`bg-gradient-to-b from-gray-800/60 to-amber-900/60 backdrop-blur-lg border-2 ${
                    bonus.premium ? 'border-yellow-400/50 shadow-yellow-400/20' : 'border-gray-600/50'
                  } rounded-2xl p-6 shadow-2xl hover:border-yellow-400/70 transition-all group relative overflow-hidden`}
                >
                  {/* Premium Badge */}
                  {bonus.premium && (
                    <div className="absolute top-3 right-3">
                      <div className="bg-gradient-to-r from-yellow-400 to-amber-500 text-black text-xs font-black px-2 py-1 rounded-full flex items-center gap-1">
                        <SafeIcon icon={FiHome} className="w-3 h-3" />
                        AGENT VIP
                      </div>
                    </div>
                  )}

                  <div className="text-center">
                    <div className={`w-16 h-16 ${
                      bonus.premium 
                        ? 'bg-gradient-to-r from-yellow-400 to-amber-500 shadow-yellow-400/50' 
                        : 'bg-gradient-to-r from-gray-600 to-gray-700'
                    } rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl`}>
                      <SafeIcon icon={bonus.icon} className={`w-8 h-8 ${bonus.premium ? 'text-black' : 'text-white'}`} />
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-sm font-black py-1 px-3 rounded-full inline-block mb-3">
                      FREE BONUS
                    </div>
                    
                    <h3 className="text-lg font-black text-white mb-3">{bonus.title}</h3>
                    <div className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 mb-3">
                      {bonus.value}
                    </div>
                    <p className="text-gray-400 text-sm leading-relaxed">{bonus.description}</p>
                  </div>

                  {/* Shimmer Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -skew-x-12 animate-shimmer opacity-0 group-hover:opacity-100 transition-opacity"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium About Instructor */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-black to-amber-900 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 to-amber-500/5"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4">
            <div className="space-y-8 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-start lg:space-y-0">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="relative group">
                  {/* Mobile Premium Image Container */}
                  <div className="w-full h-72 sm:h-80 lg:h-[420px] rounded-2xl overflow-hidden shadow-2xl border-4 border-yellow-400/30 group-hover:border-yellow-400/50 transition-all">
                    <img 
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759040594802-Abdul%20Hakkim.webp" 
                      alt="Abdul Hakkim - Real Estate AI Expert"
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-br from-yellow-100 to-amber-100 rounded-2xl hidden items-center justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-2xl">
                          <span className="text-3xl font-black text-black">AH</span>
                        </div>
                        <div className="text-xl font-black text-gray-900">Abdul Hakkim</div>
                        <div className="text-gray-700 font-medium">Real Estate AI Expert</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Mobile Premium Floating Badges */}
                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full p-3 shadow-2xl">
                    <SafeIcon icon={FiHome} className="w-6 h-6 text-black" />
                  </div>
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-3 shadow-2xl">
                    <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-white" />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="text-center lg:text-left">
                  <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-4">
                    Meet Your Real Estate AI Mentor
                  </h2>
                  <div className="text-xl font-bold text-white">Abdul Hakkim</div>
                  <div className="text-lg text-yellow-400 font-medium">Real Estate AI Transformation Expert</div>
                </div>
                
                {/* Mobile Premium Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { metric: "₹50+ Crore", label: "Property Sales", icon: FiDollarSign },
                    { metric: "2000+", label: "Agents Trained", icon: FiUsers },
                    { metric: "5 Years", label: "Real Estate AI", icon: FiCrown }
                  ].map((stat, index) => (
                    <motion.div 
                      key={index}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-xl p-4 text-center backdrop-blur-sm hover:border-yellow-400/50 transition-all"
                    >
                      <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-2">
                        <SafeIcon icon={stat.icon} className="w-5 h-5 text-black" />
                      </div>
                      <div className="text-xl font-black text-yellow-400">{stat.metric}</div>
                      <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile Premium Story */}
                <div className="space-y-4 text-gray-300 text-base leading-relaxed">
                  <p className="bg-gradient-to-r from-gray-800/50 to-amber-900/50 border border-yellow-400/20 rounded-xl p-4 backdrop-blur-sm">
                    <span className="text-yellow-400 font-bold">"Real Estate AI Revolution"</span> என் mission. 
                    2000+ agents & promoters-ஐ AI-ன் மூலம் 10X sales achieve பண்ண help பண்ணியிருக்கேன். 
                  </p>
                  <p className="bg-gradient-to-r from-gray-800/50 to-amber-900/50 border border-yellow-400/20 rounded-xl p-4 backdrop-blur-sm">
                    <span className="text-green-400 font-bold">"Proven systems"</span> - lead generation, client management, sales automation 
                    எல்லாத்தையும் <span className="text-yellow-400 font-bold"> AI-powered tools</span>-ல convert பண்ணி teach பண்ணுவேன்.
                  </p>
                  <p className="text-center text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-xl p-4">
                    "உங்க property business-ஐ next level-க்கு கொண்டுபோக ready!"
                  </p>
                </div>

                {/* Mobile Premium Credentials */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-full px-4 py-2 justify-center">
                    <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-400" />
                    <span className="text-green-300 font-bold text-sm">2000+ Agent Success Stories</span>
                  </div>
                  <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border border-yellow-400/30 rounded-full px-4 py-2 justify-center">
                    <SafeIcon icon={FiHome} className="w-4 h-4 text-yellow-400" />
                    <span className="text-yellow-300 font-bold text-sm">Real Estate AI Leader</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Mobile Premium FAQ Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-b from-amber-900 to-black relative">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/3 to-amber-500/3"></div>
          <div className="relative z-10 max-w-4xl mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-4">
                Real Estate Agents Common Questions
              </h2>
              <p className="text-lg text-gray-400">எல்லா doubts-ம் clear பண்ணிக்கோங்கள்</p>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gradient-to-r from-gray-800/50 to-amber-900/50 backdrop-blur-lg border border-yellow-400/20 rounded-xl shadow-xl hover:border-yellow-400/40 transition-all"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full px-4 sm:px-6 py-4 text-left flex justify-between items-center hover:bg-yellow-400/5 rounded-xl transition-all"
                  >
                    <span className="font-bold text-white text-base pr-4 leading-snug">{faq.question}</span>
                    <div className="flex items-center gap-2">
                      <SafeIcon 
                        icon={FiChevronDown} 
                        className={`w-5 h-5 text-yellow-400 transition-transform flex-shrink-0 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`} 
                      />
                    </div>
                  </button>
                  
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 sm:px-6 pb-4 text-gray-300 text-base leading-relaxed border-t border-yellow-400/20 pt-4">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Mobile Premium Final CTA Section */}
        <section className="py-12 sm:py-20 bg-gradient-to-br from-amber-900 via-black to-yellow-900 relative overflow-hidden">
          {/* Premium Background Effects */}
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-amber-500/10"></div>
            <FloatingElement delay={0}>
              <div className="absolute top-10 left-10 w-24 h-24 sm:w-32 sm:h-32 bg-yellow-400/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={2}>
              <div className="absolute bottom-10 right-10 w-32 h-32 sm:w-40 sm:h-40 bg-amber-500/20 rounded-full blur-2xl"></div>
            </FloatingElement>
            <FloatingElement delay={4}>
              <div className="absolute top-1/2 left-1/4 w-20 h-20 sm:w-24 sm:h-24 bg-yellow-300/15 rounded-full blur-xl"></div>
            </FloatingElement>
          </div>

          <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 leading-tight">
                Ready to 10X Your Property Sales?
              </h2>

              <p className="text-xl text-gray-300 font-medium max-w-2xl mx-auto">
                <span className="text-yellow-400 font-bold">3 மணி நேரத்தில்</span> உங்கள் real estate business-ஐ transform பண்ணுங்கள்!
              </p>

              {/* Mobile Final Premium Timer */}
              <div className="bg-gradient-to-r from-red-600/30 to-orange-600/30 backdrop-blur-lg border-2 border-red-400/50 rounded-2xl p-6 max-w-xs mx-auto shadow-2xl">
                <p className="text-red-400 font-black text-lg mb-4">⏰ Last Chance - Agent Bonuses Expire:</p>
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.minutes.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Minutes</div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500 to-orange-500 rounded-xl p-4 border-2 border-red-400">
                    <div className="text-3xl font-black text-white">{bonusTimeLeft.seconds.toString().padStart(2, '0')}</div>
                    <div className="text-sm text-red-200 uppercase font-bold">Seconds</div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 px-2">
                <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border-2 border-red-400/50 rounded-xl p-4 max-w-sm mx-auto">
                  <div className="text-red-400 font-black text-xl mb-2">
                    🚨 FINAL WARNING
                  </div>
                  <div className="text-white font-bold text-lg">
                    Only {seatsLeft} Agent Seats Left!
                  </div>
                  <div className="text-gray-300 text-sm mt-2">
                    Workshop starts in few hours. Don't miss this opportunity!
                  </div>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openRegistrationModal}
                  className="bg-gradient-to-r from-yellow-500 via-amber-600 to-yellow-700 text-black font-black py-6 px-12 rounded-2xl text-xl shadow-2xl border-4 border-yellow-300 w-full relative overflow-hidden mobile-btn"
                  style={{
                    background: 'linear-gradient(135deg, #eab308 0%, #f59e0b 25%, #d97706 50%, #eab308 75%, #f59e0b 100%)',
                    backgroundSize: '300% 300%',
                    animation: 'gradient-shift 3s ease infinite'
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-3">
                    <SafeIcon icon={FiHome} className="w-6 h-6" />
                    YES! Transform My Real Estate Business
                    <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                </motion.button>

                {/* Mobile Premium Guarantees */}
                <div className="flex flex-col gap-3">
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-400/50 rounded-full px-4 py-2 text-green-300 font-bold backdrop-blur-sm text-center">
                    <SafeIcon icon={FiShield} className="inline mr-2 w-4 h-4" />
                    100% Real Estate Success Guarantee
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1 bg-gradient-to-r from-purple-500/20 to-yellow-500/20 border-2 border-purple-400/50 rounded-full px-3 py-2 text-purple-300 font-bold backdrop-blur-sm text-center text-sm">
                      <SafeIcon icon={FiLock} className="inline mr-1 w-4 h-4" />
                      Secure Payment
                    </div>
                    <div className="flex-1 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-2 border-yellow-400/50 rounded-full px-3 py-2 text-yellow-300 font-bold backdrop-blur-sm text-center text-sm">
                      <SafeIcon icon={FiHome} className="inline mr-1 w-4 h-4" />
                      Agent-Only
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-gray-400 text-base max-w-xl mx-auto leading-relaxed">
                Join 2000+ real estate professionals who transformed their business with AI. 
                <span className="text-yellow-400 font-bold"> Your property empire starts today!</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Mobile Premium Footer */}
        <footer className="bg-black border-t border-yellow-400/20 py-8">
          <div className="max-w-7xl mx-auto px-4">
            <div className="space-y-6 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6 sm:space-y-0">
              <div className="text-center sm:text-left">
                <h3 className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-amber-500 mb-3">
                  Real Estate AI Academy
                </h3>
                <p className="text-gray-400 leading-relaxed text-sm">
                  Real estate professionals-க்கு AI-ன் மூலம் business growth achieve பண்ண உதவும் exclusive academy.
                </p>
              </div>
              
              <div className="text-center sm:text-left">
                <h4 className="text-lg font-bold text-white mb-4">Agent Support</h4>
                <div className="space-y-3 text-gray-400">
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiPhone} className="w-4 h-4 text-black" />
                    </div>
                    <span className="font-medium text-sm">+91 9884899080</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiMail} className="w-4 h-4 text-black" />
                    </div>
                    <span className="font-medium text-sm">support@yinguru.com</span>
                  </div>
                </div>
              </div>

              <div className="text-center sm:text-left lg:col-span-1 sm:col-span-2 lg:col-span-1">
                <h4 className="text-lg font-bold text-white mb-4">Legal</h4>
                <div className="space-y-2 text-gray-400 text-sm">
                  <div><a href="https://funnelsdone.com/terms" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors font-medium">Terms & Conditions</a></div>
                  <div><a href="https://funnelsdone.com/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors font-medium">Privacy Policy</a></div>
                  <div><a href="https://funnelsdone.com/refund-policy" target="_blank" rel="noopener noreferrer" className="hover:text-yellow-400 transition-colors font-medium">Refund Policy</a></div>
                </div>
              </div>
            </div>
            
            {/* Mobile Premium Disclaimer */}
            <div className="border-t border-gray-800 mt-6 pt-6">
              <div className="text-xs text-gray-500 leading-relaxed mb-4 bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                <strong className="text-yellow-400">Disclaimer:</strong> This site is not a part of the Facebook™ website or Facebook™ Inc. Additionally, This site is NOT endorsed by Facebook™ in any way. FACEBOOK™ is a trademark of FACEBOOK™, Inc. Results shown are from real estate professionals but individual results may vary based on effort and implementation. We provide proven AI strategies and tools designed specifically for property professionals, but your success depends on your commitment to applying what you learn.
              </div>
              <div className="text-center text-gray-400">
                <p className="font-medium text-sm">&copy; 2024 Real Estate AI Academy. All rights reserved.</p>
                <p className="text-xs mt-2 text-yellow-400">Transforming Property Business through AI Innovation</p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;